import { useLayoutEffect, useState } from 'react';
import { Avatar, Icon } from '../common';
import { SHIFT_TEMPLATES } from './shiftConstants';
import { computePopoverPosition, type PopoverPosition } from './popoverPosition';
import type { Staff } from './types';
import { DOW_ABBR, MONTHS_ABBR } from '../../lib/dates';
import { useClickOutside } from '../../lib/useClickOutside';

const POPOVER_W = 240;

export interface SlotInlineEditorProps {
  staff: Staff[];
  date: string; // ISO
  slot: number; // 0-7
  currentStaffId: string | null;
  // Helpers already on this period that day — offered but not pickable, so one
  // person can't fill two slots of the same period.
  disabledStaffIds: Set<string>;
  // Helpers on leave that day — flagged (still pickable; a warning, not a block).
  onLeaveStaffIds: Set<string>;
  anchorRect: DOMRect;
  onAssign: (staffId: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

// Pick a helper for a shift slot (or clear it). A fixed popover anchored under
// the clicked slot, closing on click-outside. Lifted from the prototype's
// SlotInlineEditor; flags helpers on leave (warning, not a block).
export function SlotInlineEditor({
  staff,
  date,
  slot,
  currentStaffId,
  disabledStaffIds,
  onLeaveStaffIds,
  anchorRect,
  onAssign,
  onRemove,
  onClose,
}: SlotInlineEditorProps) {
  const ref = useClickOutside<HTMLDivElement>(onClose);
  const [pos, setPos] = useState<PopoverPosition | null>(null);
  const tmpl = SHIFT_TEMPLATES[slot]!;
  const isAM = slot < 4;
  const slotInGroup = (slot % 4) + 1;
  const d = new Date(date + 'T00:00:00');

  // Position from the popover's *measured* height — opening below, flipping
  // above near the bottom edge, clamped fully into view. Runs before paint, so
  // no flash; re-measures whenever the offered helper list changes its height.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setPos(
      computePopoverPosition(anchorRect, POPOVER_W, el.offsetHeight, {
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    );
  }, [anchorRect, staff.length, currentStaffId]);

  return (
    <div
      className="inline-editor"
      ref={ref}
      style={{
        position: 'fixed',
        top: pos?.top ?? anchorRect.bottom + 4,
        left: pos?.left ?? anchorRect.left,
        width: POPOVER_W,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div className="ie-head">
        <span className={'ie-stripe ' + (isAM ? 'am' : 'pm')} />
        <div className="ie-head-text">
          <div className="ie-title">
            {DOW_ABBR[d.getDay()]} {d.getDate()} {MONTHS_ABBR[d.getMonth()]}
          </div>
          <div className="ie-subtitle mono">
            {isAM ? 'AM' : 'PM'} · slot {slotInGroup} · {tmpl.start}–{tmpl.end}
          </div>
        </div>
      </div>
      <div className="ie-list">
        {staff.length === 0 && <div className="ie-empty">No helpers yet — add one first.</div>}
        {staff.map((s) => {
          const isCurrent = s.id === currentStaffId;
          const isTaken = disabledStaffIds.has(s.id);
          const onLeave = onLeaveStaffIds.has(s.id);
          const sub = isTaken ? `Already on ${isAM ? 'AM' : 'PM'}` : onLeave ? 'On leave this day' : s.role;
          return (
            <button
              key={s.id}
              type="button"
              className={'ie-row' + (isCurrent ? ' on' : '') + (isTaken ? ' taken' : '') + (onLeave ? ' is-on-leave' : '')}
              disabled={isTaken}
              onClick={() => onAssign(s.id)}
            >
              <Avatar name={s.name} id={s.id} size={26} />
              <div className="ie-info">
                <div className="ie-name">{s.name}</div>
                <div className="ie-sub">{sub}</div>
              </div>
              {isCurrent && <Icon name="check" size={14} />}
            </button>
          );
        })}
      </div>
      {currentStaffId && (
        <div className="ie-foot">
          <button className="ie-remove" onClick={onRemove}>
            <Icon name="trash" size={13} /> Remove shift
          </button>
        </div>
      )}
    </div>
  );
}
